import { useState, useEffect, useCallback, useMemo } from "react";
import {
  Save,
  RefreshCw,
  Info,
  CheckCircle2,
  AlertTriangle,
  CalendarDays,
  Loader2,
  ChevronDown,
} from "lucide-react";
import { fetchLeaveSettings, updateLeaveSettings } from "../../Moduels/leave/leaveApi";
import { useToast } from "../../Moduels/Context/ToastContext";
import ConfirmDialog from "../ui/ConfirmDialog";
import "./LeaveCycleTab.css";

const CYCLE_TYPES = [
  { value: "calendar", label: "Calendar Year (Jan – Dec)", startMonth: 1, endMonth: 12 },
  { value: "financial", label: "Financial Year (Apr – Mar)", startMonth: 4, endMonth: 3 },
];

const MONTH_NAMES = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

function getCycleConfig(cycleType) {
  return CYCLE_TYPES.find((c) => c.value === cycleType) || CYCLE_TYPES[0];
}

function computeActiveCycle(cycleType) {
  const today = new Date();

  if (cycleType === "financial") {
    let startYear = today.getFullYear();
    if (today.getMonth() < 3) startYear -= 1;
    return {
      label: `FY ${startYear}–${String(startYear + 1).slice(2)}`,
      startDate: `01 Apr ${startYear}`,
      endDate: `31 Mar ${startYear + 1}`,
      duration: "12 Months",
    };
  }

  const year = today.getFullYear();
  return {
    label: `CY ${year}`,
    startDate: `01 Jan ${year}`,
    endDate: `31 Dec ${year}`,
    duration: "12 Months",
  };
}

export default function LeaveCycleTab({ orgId, onSaved }) {
  const toast = useToast();

  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [cycleType, setCycleType] = useState("calendar");
  const [description, setDescription] = useState("");

  const [originalCycleType, setOriginalCycleType] = useState("calendar");
  const [showConfirm, setShowConfirm] = useState(false);
  const [errors, setErrors] = useState({});
  const [showGuide, setShowGuide] = useState(false);

  const cycleConfig = useMemo(() => getCycleConfig(cycleType), [cycleType]);
  const activeCycle = useMemo(() => computeActiveCycle(cycleType), [cycleType]);
  const hasChanged = cycleType !== originalCycleType;

  const loadSettings = useCallback(async () => {
    if (!orgId) return;
    setLoading(true);
    try {
      const data = await fetchLeaveSettings(orgId);
      setSettings(data);
      const savedCycle = data.year_cycle || "calendar";
      setCycleType(savedCycle);
      setOriginalCycleType(savedCycle);

      const lc = data.extra_json?.leave_cycle || {};
      setDescription(lc.description || "");
    } catch {
      setSettings(null);
    } finally {
      setLoading(false);
    }
  }, [orgId]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const validate = useCallback(() => {
    const errs = {};
    if (!cycleType) errs.cycleType = "Leave Cycle Type is required.";
    if (description.length > 200) errs.description = "Description must be 200 characters or less.";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  }, [cycleType, description]);

  const doSave = useCallback(async () => {
    setSaving(true);
    try {
      const config = getCycleConfig(cycleType);
      const existingExtra = settings?.extra_json || {};
      const oldHistory = existingExtra.leave_cycle?.history || [];

      const newHistory = [...oldHistory];
      if (hasChanged && originalCycleType && settings) {
        const oldConfig = getCycleConfig(originalCycleType);
        newHistory.push({
          cycle_type: originalCycleType,
          start_month: oldConfig.startMonth,
          end_month: oldConfig.endMonth,
          deactivated_at: new Date().toISOString(),
          status: "inactive",
        });
      }

      const payload = {
        year_cycle: cycleType,
        extra_json: {
          ...existingExtra,
          leave_cycle: {
            description: description.trim(),
            start_month: config.startMonth,
            end_month: config.endMonth,
            version: (existingExtra.leave_cycle?.version || 0) + 1,
            history: newHistory,
            updated_at: new Date().toISOString(),
          },
        },
      };

      await updateLeaveSettings(orgId, payload);
      toast.success("Leave cycle settings saved successfully.");
      setOriginalCycleType(cycleType);
      setShowConfirm(false);
      await loadSettings();
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message || "Failed to save leave cycle settings.");
    } finally {
      setSaving(false);
    }
  }, [cycleType, description, orgId, settings, hasChanged, originalCycleType, toast, loadSettings, onSaved]);

  const handleSave = useCallback(() => {
    if (!validate()) return;
    if (hasChanged) {
      setShowConfirm(true);
    } else {
      doSave();
    }
  }, [validate, hasChanged, doSave]);

  const handleCycleTypeChange = useCallback((e) => {
    const val = e.target.value;
    setCycleType(val);
    setErrors((prev) => ({ ...prev, cycleType: undefined }));
  }, []);

  if (loading) {
    return (
      <div className="lc-loading">
        <Loader2 size={20} className="lc-spin" />
        <span>Loading leave cycle settings…</span>
      </div>
    );
  }

  return (
    <div className="lc-root">
      {/* ─── Collapsible Guide ─── */}
      <div className="lss-guide-card" style={{ marginTop: 16 }}>
        <button
          type="button"
          className="lss-guide-card-toggle"
          onClick={() => setShowGuide((v) => !v)}
        >
          <div className="lss-guide-card-toggle-left">
            <Info size={15} />
            <strong>How Leave Cycle Works</strong>
          </div>
          <ChevronDown size={16} className={`lss-guide-chevron ${showGuide ? "lss-guide-chevron--open" : ""}`} />
        </button>
        {showGuide && (
          <div className="lss-guide-card-body">
            <div className="lss-guide-step-list">
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">1</span>
                <div>
                  <strong>Choose Your Leave Cycle Type</strong>
                  <p>Select either <em>Calendar Year (Jan–Dec)</em> or <em>Financial Year (Apr–Mar)</em>. This determines when leave balances reset for all employees.</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">2</span>
                <div>
                  <strong>Start &amp; End Month Auto-Set</strong>
                  <p>Based on your selection, the start and end months are automatically configured. Calendar Year runs January to December, Financial Year runs April to March.</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">3</span>
                <div>
                  <strong>Balance Reset at Cycle End</strong>
                  <p>At the end of each leave cycle, employee leave balances are reset. Carry-forward rules (configured in Accrual Policy) determine how many unused days transfer to the next cycle.</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">4</span>
                <div>
                  <strong>Accrual Follows the Cycle</strong>
                  <p>Monthly or periodic leave accruals are calculated within this cycle. Employees joining mid-cycle receive prorated entitlements based on their joining date.</p>
                </div>
              </div>
              <div className="lss-guide-step-item">
                <span className="lss-guide-step-num">5</span>
                <div>
                  <strong>Save to Apply</strong>
                  <p>Click <em>Save Changes</em> to apply the leave cycle. Changing the cycle type mid-year will affect all future balance calculations and accruals.</p>
                </div>
              </div>
            </div>
            <p className="lss-guide-note" style={{ marginTop: 10 }}>
              The leave cycle applies organization-wide. All leave types, accrual policies, and balance resets follow this cycle.
            </p>
          </div>
        )}
      </div>

      <div className="lc-main">
        {/* Leave Cycle Card */}
        <div className="lc-card">
          <div className="lc-form">
            {/* Field 1: Leave Cycle Type */}
            <div className="lc-field">
              <label className="lc-label">
                Leave Cycle Type <span className="lc-required">*</span>
              </label>
              <select
                value={cycleType}
                onChange={handleCycleTypeChange}
                className={`lc-select ${errors.cycleType ? "lc-select--error" : ""}`}
              >
                {CYCLE_TYPES.map((ct) => (
                  <option key={ct.value} value={ct.value}>
                    {ct.label}
                  </option>
                ))}
              </select>
              {errors.cycleType && <p className="lc-error-text">{errors.cycleType}</p>}
            </div>

            {/* Fields 2 & 3: Start Month / End Month (2-col) */}
            <div className="lc-field-row">
              <div className="lc-field">
                <label className="lc-label">
                  Leave Cycle Start Month <span className="lc-required">*</span>
                </label>
                <div className="lc-readonly-field">
                  <CalendarDays size={14} className="lc-readonly-icon" />
                  {MONTH_NAMES[cycleConfig.startMonth - 1]}
                </div>
                <p className="lc-helper">The month when the leave year starts.</p>
              </div>
              <div className="lc-field">
                <label className="lc-label">
                  Leave Cycle End Month <span className="lc-required">*</span>
                </label>
                <div className="lc-readonly-field">
                  <CalendarDays size={14} className="lc-readonly-icon" />
                  {MONTH_NAMES[cycleConfig.endMonth - 1]}
                </div>
                <p className="lc-helper">The month when the leave year ends.</p>
              </div>
            </div>

            {/* Field 4: Description */}
            <div className="lc-field">
              <label className="lc-label">
                Description
              </label>
              <textarea
                value={description}
                onChange={(e) => {
                  setDescription(e.target.value);
                  setErrors((prev) => ({ ...prev, description: undefined }));
                }}
                placeholder="e.g. Organization leave year from April to March."
                className={`lc-textarea ${errors.description ? "lc-textarea--error" : ""}`}
                maxLength={200}
                rows={2}
              />
              {errors.description && <p className="lc-error-text">{errors.description}</p>}
            </div>
          </div>
        </div>

        {/* Current Active Leave Cycle */}
        <div className="lc-active-card">
          <div className="lc-active-header">
            <h4 className="lc-active-title">Current Active Leave Cycle</h4>
            <span className="lc-active-badge">
              <CheckCircle2 size={12} />
              Active
            </span>
          </div>
          <div className="lc-active-grid">
            <div className="lc-active-item">
              <span className="lc-active-label">Leave Cycle</span>
              <span className="lc-active-value">{activeCycle.label}</span>
            </div>
            <div className="lc-active-item">
              <span className="lc-active-label">Leave Year Period</span>
              <span className="lc-active-value">
                {activeCycle.startDate} &rarr; {activeCycle.endDate}
              </span>
            </div>
            <div className="lc-active-item">
              <span className="lc-active-label">Cycle Duration</span>
              <span className="lc-active-value">{activeCycle.duration}</span>
            </div>
          </div>
        </div>

        {/* Information Banner */}
        <div className="lc-info-banner">
          <div className="lc-info-icon">
            <AlertTriangle size={16} />
          </div>
          <div>
            <p className="lc-info-text">
              Changing the leave cycle will affect accrual schedules, carry forward rules and existing leave balances.
            </p>
            <p className="lc-info-subtext">
              Please ensure you review all impacts before making changes.
            </p>
          </div>
        </div>

        {/* Actions */}
        <div className="lc-actions">
          <button
            type="button"
            className="lc-btn-primary"
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? (
              <Loader2 size={14} className="lc-spin" />
            ) : (
              <Save size={14} />
            )}
            {saving ? "Saving…" : "Save Settings"}
          </button>
          <button
            type="button"
            className="lc-btn-secondary"
            onClick={loadSettings}
            disabled={saving}
          >
            <RefreshCw size={14} />
            Reload
          </button>
        </div>
      </div>

      {/* Confirm Dialog */}
      <ConfirmDialog
        open={showConfirm}
        title="Change Active Leave Cycle?"
        message="Changing the leave cycle will affect accrual schedules, carry forward rules and existing leave balances. Please ensure you review all impacts before making changes."
        confirmLabel="Confirm & Save"
        cancelLabel="Cancel"
        onConfirm={doSave}
        onCancel={() => setShowConfirm(false)}
        loading={saving}
      />
    </div>
  );
}
